import React from 'react';
import type { FC, ReactNode } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import { ToolbarButton } from '@atlaskit/editor-toolbar/toolbar-button';
import { ToolbarButtonGroup } from '@atlaskit/editor-toolbar/toolbar-button-group';
import { ToolbarTooltip } from '@atlaskit/editor-toolbar/toolbar-tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePageContentId } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

import { useInlineReactionActions } from './inlineReactionStore.ts';

const messages = defineMessages({
	reactTooltip: {
		id: 'experiment-inline-reactions.inline-reaction.tooltip',
		defaultMessage: 'React',
		description: 'Tooltip for the React button in the highlight actions toolbar',
	},
	reactLabel: {
		id: 'experiment-inline-reactions.inline-reaction.label',
		defaultMessage: 'React',
		description: 'Label for the React button in the highlight actions toolbar',
	},
});

type InlineReactionActionProps = {
	/**
	 * When false the React button is disabled to match the inline-comment
	 * eligibility gate. Unsupported selections (e.g. table headers, sync blocks,
	 * non-commentable bodied extensions) should pass false here so reactions
	 * behave consistently with inline comments.
	 *
	 * Defaults to true so callers that do not yet pass this prop are unaffected.
	 */
	isAnnotationAllowed?: boolean;
	/**
	 * Optional tooltip content to show when the button is disabled. Callers
	 * should pass the result of getReactionButtonDisabledMessage()
	 * so that the disabled reason is contextualised for reactions (e.g.
	 * "Reactions can't be added to archived items"). When omitted the default
	 * "React" tooltip is shown regardless of disabled state.
	 */
	disabledTooltip?: ReactNode;
};

export const InlineReactionAction: FC<InlineReactionActionProps> = ({
	isAnnotationAllowed = true,
	disabledTooltip,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [pageId] = usePageContentId();
	const pageMode = useGetPageMode();
	const { triggerOpen } = useInlineReactionActions();

	const tooltipContent =
		!isAnnotationAllowed && disabledTooltip
			? disabledTooltip
			: formatMessage(messages.reactTooltip);

	return (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButtonGroup>
				<ToolbarButton
					iconBefore={<EmojiAddIcon label="" size="small" />}
					testId="highlight-actions-react"
					onClick={() => {
						if (pageId) {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'createInlineReactionFromHighlightActionsMenu',
									source: 'highlightActionsMenu',
									attributes: {
										contentId: pageId,
										pageMode,
										isInlineReaction: true,
									},
								},
							}).fire();
						}
						void triggerOpen();
					}}
					isDisabled={!isAnnotationAllowed}
				>
					<FormattedMessage {...messages.reactLabel} />
				</ToolbarButton>
			</ToolbarButtonGroup>
		</ToolbarTooltip>
	);
};
